import firebase from 'firebase/app';
import i18n from '@/i18n';
import { setToken, removeToken } from '@/services/network/api';
import { successAction, failureAction } from '@/store/actionTypes';
import { SAVE_TARGETING_INFO, GET_TARGETING_INFO } from '@/store/targetingInfo/actionTypes';
import { getDefaultTargetingInfo } from '@/services/targetingInfo';
import {
  getPrivateInfoForSignUp,
  getAdditionalDataAccordingToRoute,
  getUserMetadata,
} from '@/services/privateInfo';
import {
  SHOW_NOTIFICATION,
  SHOW_DEFAULT_ERROR_NOTIFICATION,
} from '@/store/notification/actionTypes';
import { SAVE_PRIVATE_INFO, GET_PRIVATE_INFO } from '@/store/privateInfo/actionTypes';
import { showSignUpFailedDialog } from '@/services/dialogs';
import router from '@/router';
import { ROUTER_PATHS } from '@/router/constants';
import { broadcast as broadcastToTabs, ACTIONS } from '@/services/tabs';
import { getDisplayName, isCurrentAccountTest, setAuthorizeStatus } from '@/services/user';
import { isSafeConnection } from '@/services/connection';
import logger from '@/services/logger';
import {
  SESSION_STARTED,
  SESSION_FINISHED,
  SIGN_UP,
  SIGN_IN,
  RESTORE_PASSWORD_EMAIL,
  CREATE_PASSWORD,
  CHANGE_PASSWORD,
  RELOAD_USER,
  SEND_VERIFICATION_EMAIL,
  EMAIL_VERIFIED,
  SOCIAL_SIGN_IN,
} from './actionTypes';

async function checkSecureConnection() {
  if (!(await isSafeConnection())) {
    const error = { message: i18n.t('signup.not.secure.connection'), type: 'connection.insecure' };
    return error;
  }

  return null;
}

export default {
  async [SESSION_STARTED]({ dispatch }, user) {
    try {
      await setAuthorizeStatus(true);
      const token = await user.getIdToken();
      setToken(token);
      return dispatch(successAction(SESSION_STARTED), user);
    } catch (e) {
      dispatch(SHOW_DEFAULT_ERROR_NOTIFICATION);
      return Promise.reject(e);
    }
  },
  async [SESSION_FINISHED]({ dispatch }) {
    try {
      await setAuthorizeStatus(false);
      logger.log({
        type: 'SESSION_FINISHED',
      });
      return await firebase.auth().signOut();
    } catch (e) {
      dispatch(SHOW_DEFAULT_ERROR_NOTIFICATION);
      return Promise.reject(e);
    } finally {
      removeToken();
      dispatch(successAction(SESSION_FINISHED));
    }
  },
  async [SIGN_IN]({ dispatch }, data) {
    try {
      const result = await firebase.auth().signInWithEmailAndPassword(data.email, data.password);
      dispatch(successAction(SIGN_IN), result.user);
      return result;
    } catch (error) {
      dispatch(failureAction(SIGN_IN));
      throw new Error(error);
    }
  },
  async [SOCIAL_SIGN_IN]({ dispatch }, provider) {
    const securityError = await checkSecureConnection();
    if (securityError) {
      dispatch(failureAction(SIGN_UP), { ...securityError, social: true });
      throw new Error(securityError);
    }

    const result = await firebase.auth().signInWithPopup(provider);
    const isNew = Boolean(result?.additionalUserInfo?.isNewUser);

    if (isNew) {
      const { user } = result;
      if (user?.email) {
        dispatch(successAction(SIGN_UP), { ...result, originUserData: user });
      } else {
        // NOTE: This is workaround because we cannot prevent sign up for accounts without email
        if (user) {
          await logger.anonymousLog({
            type: 'SOCIAL_SIGN_IN_FAILED',
            message: 'Account was deleted according to email restrictions',
            userId: user.uid,
          });
          firebase.auth().currentUser.delete();
        }
        // END

        dispatch(failureAction(SIGN_UP), {
          message: i18n.t('signup.email.error'),
          type: 'email/required',
          social: true,
        });
      }
    } else {
      dispatch(successAction(SIGN_IN), result.user);
    }

    return result;
  },
  async [successAction(SIGN_IN)]({ dispatch }, user) {
    const { lastSignedInAt } = getUserMetadata(user);
    const token = await user.getIdToken();
    setToken(token);
    return dispatch(SAVE_PRIVATE_INFO, { lastSignedInAt });
  },
  [RESTORE_PASSWORD_EMAIL]({ dispatch }, { email, message }) {
    return firebase
      .auth()
      .sendPasswordResetEmail(email)
      .then(() => {
        const notification = {
          type: 'info',
          message,
        };

        dispatch(SHOW_NOTIFICATION, notification);
      });
  },
  async [SIGN_UP]({ dispatch }, data) {
    const queryData = getAdditionalDataAccordingToRoute();
    const securityError = await checkSecureConnection();
    if (securityError) {
      dispatch(failureAction(SIGN_UP), { ...securityError, social: false, email: data.email });
      throw new Error(securityError);
    }

    try {
      const result = await firebase
        .auth()
        .createUserWithEmailAndPassword(data.email, data.password);

      const displayName = getDisplayName(data);
      if (displayName) {
        await firebase.auth().currentUser.updateProfile({
          displayName,
        });
      }
      return dispatch(successAction(SIGN_UP), { ...result, originUserData: data, queryData });
    } catch (error) {
      dispatch(failureAction(SIGN_UP), { ...error, social: false, email: data.email });
      throw new Error(error);
    }
  },
  async [successAction(SIGN_UP)]({ dispatch }, data) {
    if (window.fbq) {
      window.fbq('track', 'CompleteRegistration');
    }
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'signup-complete' });
    }

    const user = firebase.auth().currentUser;

    logger.log({
      type: 'SIGNUP_SUCCESS',
    });

    dispatch(SEND_VERIFICATION_EMAIL);

    const token = await user.getIdToken();

    setToken(token);

    const targetingInfoProcessingPromise = dispatch(
      SAVE_TARGETING_INFO,
      getDefaultTargetingInfo(),
    ).then(() => dispatch(GET_TARGETING_INFO));

    const privateInfoProcessingPromise = dispatch(
      SAVE_PRIVATE_INFO,
      getPrivateInfoForSignUp(data),
    ).then(() => dispatch(GET_PRIVATE_INFO));

    return Promise.all([targetingInfoProcessingPromise, privateInfoProcessingPromise]).then(
      async () => {
        if (isCurrentAccountTest()) {
          await dispatch(SESSION_FINISHED);
          router.push(ROUTER_PATHS.LOGIN);
        }
      },
    );
  },
  async [failureAction(SIGN_UP)](store, error) {
    logger.anonymousLog({ type: 'SIGNUP_FAILED', error });
    store.dispatch(SESSION_FINISHED);
    /*
        NOTE:
          Wait for the previous redirect end and start a new one.
          Reproduces in case of immediately redirect to signup after error caused by logic for RW-241 task
      */
    setTimeout(() => router.push(ROUTER_PATHS.SIGN_UP));
    // END NOTE
    showSignUpFailedDialog(error?.code || error?.message);
  },
  async [CREATE_PASSWORD](store, data) {
    return firebase
      .auth()
      .confirmPasswordReset(data.code, data.newPassword)
      .then(() => {
        const options = {
          type: 'success',
          message: i18n.t('password.changed'),
        };

        store.dispatch(SHOW_NOTIFICATION, options);
      });
  },
  async [CHANGE_PASSWORD](store, newPassword) {
    return firebase
      .auth()
      .currentUser.updatePassword(newPassword)
      .then(() => {
        const options = {
          type: 'success',
          message: i18n.t('password.changed'),
        };

        store.dispatch(SHOW_NOTIFICATION, options);
      });
  },
  async [SEND_VERIFICATION_EMAIL]({ dispatch }, { silently = false } = {}) {
    const user = firebase.auth().currentUser;

    if (user.emailVerified) {
      if (!silently) {
        dispatch(SHOW_NOTIFICATION, {
          type: 'error',
          message: i18n.t('email.verified'),
        });
      }
      throw new Error('Email is already verified');
    }

    try {
      const res = await user.sendEmailVerification();

      if (!silently) {
        dispatch(SHOW_NOTIFICATION, {
          type: 'info',
          message: i18n.t('verify.email.sent'),
        });
      }

      return res;
    } catch (error) {
      if (!silently) {
        dispatch(SHOW_DEFAULT_ERROR_NOTIFICATION);
      }

      throw new Error(error);
    }
  },
  async [RELOAD_USER]({ dispatch }, { broadcast = false } = {}) {
    await firebase.auth().currentUser?.reload();
    if (broadcast) {
      broadcastToTabs('action', { type: ACTIONS.RELOAD_USER });
    }
    dispatch(SESSION_STARTED, firebase.auth().currentUser);
  },
  [EMAIL_VERIFIED]({ dispatch }) {
    // we need to do this to update emailVerified flag
    dispatch(RELOAD_USER, { broadcast: true });
    dispatch(SAVE_PRIVATE_INFO, { emailVerified: true });
    logger.log({
      type: 'EMAIL_VERIFIED',
      message: 'User verified email',
      email: firebase.auth().currentUser?.email,
    });
  },
};
